import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';

import './Button.css';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost';

interface BaseProps {
  variant?: ButtonVariant;
  /** Trailing ▸ terminator, as on the reference's action cells. */
  marker?: boolean;
  block?: boolean;
  children: ReactNode;
  className?: string;
}

type ButtonProps = BaseProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseProps> & {
    /** Internal route. Uses the router, so no full page load. */
    to?: string;
    /** External or in-page target. */
    href?: string;
  };

export default function Button({
  variant = 'primary',
  marker = false,
  block = false,
  children,
  className,
  to,
  href,
  ...rest
}: ButtonProps) {
  const classes = [
    'btn',
    `btn--${variant}`,
    block ? 'btn--block' : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const content = (
    <>
      <span>{children}</span>
      {marker ? (
        <span className="btn__marker" aria-hidden="true">
          ▸
        </span>
      ) : null}
    </>
  );

  if (to) {
    return (
      <Link className={classes} to={to}>
        {content}
      </Link>
    );
  }

  if (href) {
    return (
      <a className={classes} href={href}>
        {content}
      </a>
    );
  }

  return (
    <button type="button" className={classes} {...rest}>
      {content}
    </button>
  );
}
